// =============================================================================
// ShelterEditor — THE canonical, reusable single-service editor
// =============================================================================
// The four-tab DFV Service editor (`ShelterDetail`) PLUS all its wiring, packaged as
// ONE mountable component. Plan + rationale: docs/telemarketing-service-editor-plan.md.
//
// ★ REUSE THIS. Any new screen/module that needs to view or edit a DFV Service must
//   mount `<ShelterEditor shelterId=… context=… onClose=… />` (SELF-SUFFICIENT mode
//   below). Do NOT re-wire `ShelterDetail` from scratch, and do NOT copy the parallel
//   wiring in AdminPages — that path is legacy (see the note there). Reusing this is
//   what keeps the DFV-Service editor from getting re-tangled into each new module.
//
// TWO MODES:
//   • SELF-SUFFICIENT (the one to use): given `shelterId` (or `shelterCode`) + `context`
//     + `onClose`, `ShelterEditorContainer` loads that one service's data, owns the
//     detail state, runs its OWN `useUserAdminHandlers` instance, and renders
//     ShelterDetail. Admin-context consumers may also pass `onDeactivateShelter` /
//     `onReactivateShelter` (telemarketing hides those, so it omits them). This is what
//     the telemarketing call modal mounts, and what future modules should mount.
//   • ADMIN PASS-THROUGH (legacy): when `shelter` (the full prop bag) is present, we
//     just render ShelterDetail with it. This exists ONLY so the current admin route
//     (AdminPages) keeps working unchanged.
//
// ⚠️ KNOWN, DELIBERATE DUPLICATION (John, 9 Aug 2026): the admin route still assembles
// the editor via its own inline wiring in AdminPages (the pass-through above); this
// component has a second, self-sufficient copy. That's TWO copies of the *wiring*
// (both render the same single `ShelterDetail`). It is bounded at two and does NOT grow
// per module, because new modules reuse the self-sufficient path here. Converging the
// admin route onto this component ("Part D") was assessed and DEFERRED: it's a large,
// hard-to-test refactor of the live admin console for a code-tidiness gain, and doing
// it once we're live is riskier than the duplication. If you EVER add a prop to
// `ShelterDetail`, wire it in BOTH places (here AND AdminPages). See the plan doc §Part D.
import React from "react";
import { useSession } from "../../app/SessionContext.jsx";
import { listActiveByShelterId } from "../../data/deliveryAddresses";
import { listLookupOptions, listLookupValues } from "../../data/lookups";
import { listShelterProfiles, listStaffProfiles } from "../../data/profiles";
import { listAllShelters } from "../../data/shelters";
import { listAllUserShelters } from "../../data/userShelters";
import { useUserAdminHandlers } from "../../hooks/useUserAdminHandlers";
import { assessUserDeactivation } from "../../services/accountLifecycle";
import { ROLES } from "../../utils/enums";
import { DeactivateUserConfirmModal } from "./DeactivateUserConfirmModal.jsx";
import { NewUserModal } from "./NewUserModal.jsx";
import { ShelterDetail } from "./ShelterDetail.jsx";

// A fully-blank New User form (mirrors AdminPages' newForm seed) so createUser +
// NewUserModal behave the same on the self-sufficient path.
const BLANK_NEW_USER_FORM = {
  email: "",
  password: "",
  first_name: "",
  last_name: "",
  shelter_name: "",
  shelter_code: "",
  role: "shelter",
  state: "",
  customer_type: "",
  sales_group: "",
  taxable: false,
  stock_held: false,
  phone: "",
  primary_contact_label: "DFV Service Manager",
  postal_line1: "",
  postal_suburb: "",
  postal_postcode: "",
  internal_notes: "",
  delivery_line1: "",
  delivery_line2: "",
  delivery_suburb: "",
  delivery_state: "",
  delivery_postcode: "",
  delivery_instructions: "",
  delivery_address_name: "",
};

export function ShelterEditor(props) {
  // Admin pass-through: the parent already built the full prop bag.
  if (props.shelter) return <ShelterDetail {...props} />;
  // Self-sufficient (telemarketing): load + wire from a shelter id/code.
  return <ShelterEditorContainer {...props} />;
}

// Self-sufficient container — reproduces AdminPages' single-service detail wiring.
function ShelterEditorContainer({
  shelterCode,
  shelterId,
  context = "telemarketing",
  onClose,
  // Real shelter Deactivate/Reactivate handlers (admin passes these — the cascade
  // confirm lives in AdminPages, which owns the list the cascade affects). Omitted in
  // telemarketing, where those buttons are hidden anyway → harmless no-op.
  onDeactivateShelter,
  onReactivateShelter,
}) {
  const { user } = useSession();

  const [shelters, setShelters] = React.useState([]);
  const [caseworkers, setCaseworkers] = React.useState([]);
  const [staffRows, setStaffRows] = React.useState([]);
  const [memberships, setMemberships] = React.useState([]);
  const [customerTypes, setCustomerTypes] = React.useState([]);
  const [salesGroups, setSalesGroups] = React.useState([]);
  const [warehouses, setWarehouses] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [savedMsg, setSavedMsg] = React.useState("");

  // Delivery-address state (owned here; the hook receives values + setters).
  const [showDeliveryModal, setShowDeliveryModal] = React.useState(null);
  const [deliveryAddrs, setDeliveryAddrs] = React.useState([]);
  const [deliveryLoading, setDeliveryLoading] = React.useState(false);
  const [deliverySaving, setDeliverySaving] = React.useState(false);
  const [editingAddr, setEditingAddr] = React.useState(null);
  const [addrForm, setAddrForm] = React.useState({});

  // User-CRUD state (Users tab).
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [newForm, setNewForm] = React.useState(BLANK_NEW_USER_FORM);
  const [showNew, setShowNew] = React.useState(false);
  const [newUserOverride, setNewUserOverride] = React.useState(null);
  const [newSaving, setNewSaving] = React.useState(false);
  const [newErr, setNewErr] = React.useState("");
  const [showPass, setShowPass] = React.useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = React.useState(null);

  function loadUsers() {
    setLoading(true);
    const p1 = listAllShelters().then(function (rows) {
      if (rows) setShelters(rows);
    });
    const p2 = listShelterProfiles();
    const p3 = listStaffProfiles();
    // db-189: include deactivated login-less contacts so the "Show Deactivated
    // Contacts" toggle works (default-hidden in ShelterDetail). Mirrors AdminPages.
    const p4 = listAllUserShelters({ includeDeactivated: true });
    Promise.all([p1, p2, p3, p4]).then(function (results) {
      const shelterProfiles = results[1] || [];
      const staffProfiles = results[2] || [];
      const userSheltersRows = results[3] || [];
      // user_id -> shelter_codes + the PRIMARY code/id (user_shelters.is_primary is
      // the source of truth; fall back to the first membership). Mirrors AdminPages.
      const usByUser = {};
      const primaryByUser = {};
      const primaryIdByUser = {};
      userSheltersRows.forEach(function (row) {
        if (!usByUser[row.user_id]) usByUser[row.user_id] = [];
        usByUser[row.user_id].push(row.shelter_code);
        if (row.is_primary || !primaryByUser[row.user_id]) {
          primaryByUser[row.user_id] = row.shelter_code;
          primaryIdByUser[row.user_id] = row.shelter_id;
        }
      });
      shelterProfiles.forEach(function (p) {
        if (usByUser[p.id]) p.shelter_codes = usByUser[p.id];
        if (primaryByUser[p.id]) p.primaryShelterCode = primaryByUser[p.id];
        if (primaryIdByUser[p.id]) p.primaryShelterId = primaryIdByUser[p.id];
      });
      staffProfiles.forEach(function (p) {
        if (usByUser[p.id]) p.shelter_codes = usByUser[p.id];
        if (primaryByUser[p.id]) p.primaryShelterCode = primaryByUser[p.id];
        if (primaryIdByUser[p.id]) p.primaryShelterId = primaryIdByUser[p.id];
      });
      setCaseworkers(shelterProfiles);
      setStaffRows(staffProfiles);
      setMemberships(userSheltersRows);
      setLoading(false);
    });
  }

  function loadLookups() {
    listLookupValues("customer_type").then(function (vals) {
      if (vals) setCustomerTypes(vals);
    });
    listLookupValues("sales_group").then(function (vals) {
      if (vals) setSalesGroups(vals);
    });
    // Warehouse dropdown STORES the Unleashed code (external_ref) but SHOWS the name.
    listLookupOptions("warehouse").then(function (opts) {
      if (opts)
        setWarehouses(
          opts.map(function (o) {
            return { value: o.external_ref || o.value, label: o.value };
          })
        );
    });
  }

  React.useEffect(function () {
    loadUsers();
    loadLookups();
  }, []);

  function showSaved(msg) {
    setSavedMsg(msg || "Saved ✓");
    setTimeout(function () {
      setSavedMsg("");
    }, 3000);
  }

  function fv(f, key) {
    return f[key] !== undefined ? f[key] : "";
  }
  function setF(key, val) {
    setForm(function (f) {
      return Object.assign({}, f, { [key]: val });
    });
  }

  // startEdit — faithfully reproduced from AdminPages (contact-model 3c-iii + THE
  // WALL: a caseworker's name/email/phone come from the user_shelters membership,
  // never profiles.staff_*). editShelterId scopes the per-service contact.
  function startEdit(u, table, editShelterId) {
    if (u.noLogin) {
      const nm = (memberships || []).find(function (m) {
        return m.id === u.membershipId;
      });
      setEditing({
        table: "nologin",
        membershipId: u.membershipId,
        shelterId: editShelterId || undefined,
        originalEmail: nm?.email || "",
        // Prior name — so a person-level fan-out matches the same person, never a
        // stranger sharing a mailbox (contact-fan-out-shared-mailbox-fix-plan).
        originalFirstName: nm?.first_name || "",
        originalLastName: nm?.last_name || "",
      });
      setForm({
        first_name: nm?.first_name || "",
        last_name: nm?.last_name || "",
        user_email: nm?.email || "",
        phone: nm?.phone || "",
        mobile: nm?.mobile || "",
        contact_label: nm?.contact_label || "",
        role: "shelter",
        no_login: true,
      });
      return;
    }
    const editMembership = editShelterId
      ? (memberships || []).find(function (m) {
          return m.user_id === u.id && m.shelter_id === editShelterId;
        })
      : null;
    const contactEmailRow =
      editMembership ||
      (u.role === ROLES.SHELTER
        ? (memberships || []).find(function (m) {
            return m.user_id === u.id && m.email;
          })
        : null);
    const seededEmail = contactEmailRow?.email || u.staff_user_email || "";
    setEditing({
      id: u.id,
      table: table || "profiles",
      originalEmail: seededEmail,
      shelterId: editShelterId || undefined,
    });
    const contactNameRow =
      editMembership ||
      (u.role === ROLES.SHELTER
        ? (memberships || []).find(function (m) {
            return m.user_id === u.id && (m.first_name || m.last_name);
          })
        : null);
    setForm({
      first_name: contactNameRow ? contactNameRow.first_name || "" : u.staff_first_name || "",
      last_name: contactNameRow ? contactNameRow.last_name || "" : u.staff_last_name || "",
      contact_label:
        editMembership?.contact_label || (u.role === ROLES.SHELTER ? "DFV Service Manager" : ""),
      phone: editMembership?.phone || "",
      mobile: editMembership?.mobile || "",
      display_name: u.display_name || "",
      shelter_name: u.shelter_name || "",
      shelter_code: (u.primaryShelterCode ?? u.shelter_code) || "",
      role: u.role || "shelter",
      login_method: u.login_method || "password",
      staff_mobile: u.staff_mobile || "", // allow-staff-write (edit-form seed, not a DB write; Zephyr-Staff path)
      staff_colour: u.staff_colour || "",
      // "Display Name" (db-184) — the short public name shown to shelters. Wired in BOTH form-inits
      // (here + AdminPages) per the ShelterEditor duplication rule.
      preferred_name: u.preferred_name || "",
      show_on_staff_list: u.show_on_staff_list !== false,
      is_consultant: u.is_consultant === true,
      shelter_codes: Array.isArray(u.shelter_codes)
        ? u.shelter_codes
        : (u.primaryShelterCode ?? u.shelter_code)
          ? [u.primaryShelterCode ?? u.shelter_code]
          : [],
      state: u.state || "",
      user_email: seededEmail,
      no_login: false,
      customer_type: u.customer_type || "",
      taxable: u.taxable === true || u.taxable === null,
      sales_group: u.sales_group || "",
      postal_line1: u.postal_line1 || "",
      postal_line2: u.postal_line2 || "",
      postal_suburb: u.postal_suburb || "",
      postal_suburb_city: u.postal_suburb_city || "",
      postal_state: u.postal_state || "",
      postal_postcode: u.postal_postcode || "",
      delivery_instructions: u.delivery_instructions || "",
      unleashed_guid: u.unleashed_guid || "",
      internal_notes: u.internal_notes || "",
      date_onboarded: u.date_onboarded || "",
      is_generic: u.is_generic || false,
    });
    const editShelterIdForAddrs = u.primaryShelterId ?? (u.shelter_code ? u.id : null);
    if (editShelterIdForAddrs) {
      setDeliveryLoading(true);
      listActiveByShelterId(editShelterIdForAddrs).then(function (rows) {
        setDeliveryAddrs(rows || []);
        setDeliveryLoading(false);
      });
    } else {
      setDeliveryAddrs([]);
    }
  }

  const _adminHandlers = useUserAdminHandlers({
    user: user,
    addrForm: addrForm,
    showDeliveryModal: showDeliveryModal,
    editingAddr: editingAddr,
    deliveryAddrs: deliveryAddrs,
    setShowDeliveryModal: setShowDeliveryModal,
    setDeliveryLoading: setDeliveryLoading,
    setEditingAddr: setEditingAddr,
    setDeliveryAddrs: setDeliveryAddrs,
    setAddrForm: setAddrForm,
    setDeliverySaving: setDeliverySaving,
    showSaved: showSaved,
    form: form,
    editing: editing,
    newForm: newForm,
    activeSection: "shelters",
    setSaving: setSaving,
    setEditing: setEditing,
    setNewErr: setNewErr,
    setNewSaving: setNewSaving,
    setShowNew: setShowNew,
    setNewForm: setNewForm,
    setConfirmDeactivate: setConfirmDeactivate,
    loadUsers: loadUsers,
  });
  const openDeliveryModal = _adminHandlers.openDeliveryModal;
  const attachUserToShelter = _adminHandlers.attachUserToShelter;
  const detachUserFromShelter = _adminHandlers.detachUserFromShelter;
  const removeNoLoginContact = _adminHandlers.removeNoLoginContact;
  const reactivateNoLoginContact = _adminHandlers.reactivateNoLoginContact;
  const startNewAddr = _adminHandlers.startNewAddr;
  const startEditAddr = _adminHandlers.startEditAddr;
  const saveAddr = _adminHandlers.saveAddr;
  const reactivateAddr = _adminHandlers.reactivateAddr;
  const promoteAddr = _adminHandlers.promoteAddr;
  const setAddrBanned = _adminHandlers.setAddrBanned;
  const setAddrSilent = _adminHandlers.setAddrSilent;
  const deleteAddr = _adminHandlers.deleteAddr;
  const setDefault = _adminHandlers.setDefault;
  const saveEdit = _adminHandlers.saveEdit;
  const sendPasswordReset = _adminHandlers.sendPasswordReset;
  const createUser = _adminHandlers.createUser;
  const deactivateUser = _adminHandlers.deactivateUser;
  const deactivateShelters = _adminHandlers.deactivateShelters;

  const detailShelter = shelterId
    ? shelters.find(function (s) {
        return s.id === shelterId;
      })
    : shelters.find(function (s) {
        return s.shelter_code === shelterCode;
      });

  // Attached users + login-less contacts for this service (mirrors AdminPages).
  const detailUserIds = detailShelter
    ? new Set(
        memberships
          .filter(function (m) {
            return m.shelter_code === detailShelter.shelter_code;
          })
          .map(function (m) {
            return m.user_id;
          })
      )
    : null;
  const detailNoLoginRows = detailShelter
    ? memberships
        .filter(function (m) {
          return m.shelter_code === detailShelter.shelter_code && !m.user_id;
        })
        .map(function (m) {
          const nm = [m.first_name, m.last_name].filter(Boolean).join(" ");
          return {
            id: `nologin:${m.id}`,
            noLogin: true,
            membershipId: m.id,
            user_id: null,
            role: "shelter",
            // DISPLAY SHIM (mirrors AdminPages.detailNoLoginRows): the Users-tab row
            // reads staff_* for its display, so map the membership CONTACT into those
            // keys for this synthetic IN-MEMORY row. NOT a DB write — no profile row
            // is touched here (THE WALL: real writes go through the guarded saveEdit).
            staff_first_name: m.first_name, // allow-staff-write
            staff_last_name: m.last_name, // allow-staff-write
            staff_user_email: m.email, // allow-staff-write
            display_name: nm || m.email || "(no-login contact)",
            deactivated: !!m.deactivated, // db-189: real soft-delete state drives the row
          };
        })
    : [];
  const detailUsers = (
    detailUserIds
      ? caseworkers.concat(staffRows).filter(function (p) {
          return detailUserIds.has(p.id);
        })
      : []
  ).concat(detailNoLoginRows);

  const confirmDeactivateOrphans =
    confirmDeactivate && !confirmDeactivate.deactivated
      ? assessUserDeactivation(
          confirmDeactivate.id,
          memberships,
          caseworkers.concat(staffRows)
        ).orphanedShelters.map(function (code) {
          const s = shelters.find(function (sh) {
            return sh.shelter_code === code;
          });
          return { id: s?.id, shelter_code: code, shelter_name: s?.shelter_name || code };
        })
      : [];

  if (loading && !detailShelter) {
    return <div className="text-center text-zephyr-muted p-10">Loading…</div>;
  }
  if (!detailShelter) {
    return <div className="text-center text-zephyr-muted p-10">DFV Service not found.</div>;
  }

  return (
    <div>
      {savedMsg && (
        <div className="text-[13px] text-[#2E7D32] font-semibold mb-2 px-2">{savedMsg}</div>
      )}
      <ShelterDetail
        context={context}
        shelter={detailShelter}
        customerTypes={customerTypes}
        salesGroups={salesGroups}
        warehouses={warehouses}
        attachedUsers={detailUsers}
        memberships={memberships}
        deliveryModalShelter={showDeliveryModal}
        onBack={onClose}
        onDeactivateShelter={onDeactivateShelter || (() => {})}
        onReactivateShelter={onReactivateShelter || (() => {})}
        openDeliveryModal={openDeliveryModal}
        deliveryAddrs={deliveryAddrs}
        deliveryLoading={deliveryLoading}
        editingAddr={editingAddr}
        setEditingAddr={setEditingAddr}
        addrForm={addrForm}
        setAddrForm={setAddrForm}
        deliverySaving={deliverySaving}
        saveAddr={saveAddr}
        startNewAddr={startNewAddr}
        startEditAddr={startEditAddr}
        promoteAddr={promoteAddr}
        setAddrBanned={setAddrBanned}
        setAddrSilent={setAddrSilent}
        deleteAddr={deleteAddr}
        reactivateAddr={reactivateAddr}
        setDefault={setDefault}
        usersAdmin={{
          editing: editing,
          form: form,
          setForm: setForm,
          fv: fv,
          setF: setF,
          saving: saving,
          saveEdit: saveEdit,
          startEdit: startEdit,
          cancelEdit: function () {
            setEditing(null);
          },
          sendPasswordReset: sendPasswordReset,
          setConfirmDeactivate: setConfirmDeactivate,
          shelters: shelters,
          caseworkers: caseworkers,
          onNewUser: function () {
            setNewForm(function (f) {
              return Object.assign({}, f, {
                shelter_code: detailShelter.shelter_code || "",
                role: "shelter",
                no_login: false,
              });
            });
            setNewUserOverride({ lockShelter: detailShelter.shelter_code || "" });
            setShowNew(true);
          },
          onAttachUser: function (userId) {
            const contact = (memberships || []).find(function (m) {
              return m.user_id === userId && m.email;
            });
            attachUserToShelter(userId, detailShelter.shelter_code || "", contact?.email);
          },
          onDetachUser: function (u) {
            detachUserFromShelter(u, detailShelter.shelter_code || "");
          },
          onRemoveNoLogin: function (u) {
            removeNoLoginContact(u);
          },
          onReactivateNoLogin: function (u) {
            reactivateNoLoginContact(u);
          },
        }}
        onSaved={function (msg) {
          showSaved(msg);
          loadUsers();
        }}
        onMembershipsChanged={loadUsers}
      />

      {/* + New User (caseworker) with the service locked — the Users-tab "+ New User". */}
      <NewUserModal
        open={showNew}
        onClose={function () {
          setShowNew(false);
          setNewUserOverride(null);
        }}
        kind="caseworkers"
        lockShelter={newUserOverride ? newUserOverride.lockShelter : ""}
        newForm={newForm}
        setNewForm={setNewForm}
        newSaving={newSaving}
        newErr={newErr}
        setNewErr={setNewErr}
        showPass={showPass}
        setShowPass={setShowPass}
        createUser={createUser}
        shelters={shelters}
        customerTypes={customerTypes}
        salesGroups={salesGroups}
      />

      {/* Deactivate-user confirm (Users tab). */}
      <DeactivateUserConfirmModal
        user={confirmDeactivate}
        orphanedShelters={confirmDeactivateOrphans}
        onClose={function () {
          setConfirmDeactivate(null);
        }}
        onConfirm={function (u, opts) {
          deactivateUser(u);
          if (opts?.alsoDeactivateShelters) {
            const toDeactivate = confirmDeactivateOrphans.filter(function (o) {
              return o.id;
            });
            if (toDeactivate.length) deactivateShelters(toDeactivate);
          }
        }}
      />
    </div>
  );
}
